import { useFocusEffect } from '@react-navigation/native';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useNavigationOptions } from 'app/hooks/navigation';
import { useStoredValue } from 'app/lib/storage';
import { StorageKey } from 'app/lib/storageKeys';
import { type DailyActionType, useDailyStats } from 'app/network/daily-stats';
import { useUser } from 'app/network/user';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type ColorValue, Pressable } from 'react-native';

import { useUISize } from 'app/utils/web-helpers';

import { ContributionBottomSheet } from './contribution-bottom-sheet';
import { DaysRow } from './days-row';
import { EmptyStreakState } from './empty-streak-state';
import { STREAK_DEV_MOCK_ENABLED } from './streak-dev-mock';
import { TipsCard } from './tips-card';
import { NEIGHBOURHOOD_FIRST_SHARE_PERCENT } from './types';
import { useStreakContributionUi } from './use-streak-contribution-ui';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconInfoCircle, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface DailyContributionsScreenProps {
  navigation: SpecificNavigationProp<'streak'>;
}

function getDailyHeaderBackground(showIceCube: boolean, streakMuted: boolean, hasNeverContributed: boolean) {
  if (showIceCube) return theme.colors['surface.info.subtle'];
  if (streakMuted || hasNeverContributed) return theme.colors['surface.tertiary-neutral'];
  return theme.colors['surface.brand.secondary-3.subtle'];
}

interface DailyHeaderProps {
  completedDays: number[];
  currentDay: number;
  hasContributedToday: boolean;
  hasNeverContributed: boolean;
  headerBackground: ColorValue;
  missedDays: number[];
  numStreak: number;
  onOpenDetails: () => void;
  showIceCube: boolean;
  streakMuted: boolean;
  totalDays: number;
}

function DailyHeader({
  completedDays,
  currentDay,
  hasContributedToday,
  hasNeverContributed,
  headerBackground,
  missedDays,
  numStreak,
  onOpenDetails,
  showIceCube,
  streakMuted,
  totalDays,
}: Readonly<DailyHeaderProps>) {
  const { t } = useTranslation('streak');
  const widgetBackground = showIceCube
    ? 'icon.info'
    : streakMuted || hasNeverContributed
      ? 'surface.secondary-neutral'
      : 'white';
  const headerEmoji = hasContributedToday ? '🔥' : showIceCube ? '🧊' : '🔥';

  return (
    <Column
      style={{ backgroundColor: headerBackground }}
      gap="spacing.general.x4"
      paddingHorizontal="spacing.general.x4"
      paddingTop="spacing.general.x4"
      paddingBottom="spacing.general.x5"
    >
      <Row alignItems="flex-start" justifyContent="space-between">
        <Column flex={1} gap="spacing.general.x1">
          <Row alignItems="center" gap="spacing.general.x0,5">
            <Text fontSize={15} fontWeight="500">
              {t('dailyContributions')}
            </Text>
            <Pressable hitSlop={10} onPress={onOpenDetails}>
              <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.placeholder']} />
            </Pressable>
          </Row>
          <Text fontSize={28} fontWeight="bold">
            {t('day', { day: currentDay })}
          </Text>
          <Row alignItems="center" flexWrap="wrap" gap="spacing.general.x4" marginTop="spacing.general.x1">
            <Text color="text.placeholder" fontSize={14} fontWeight="600">
              {t('currentStreakLabel')}: {numStreak}
            </Text>
          </Row>
        </Column>

        <Column
          alignItems="center"
          backgroundColor={widgetBackground}
          borderRadius="radius.xxl"
          height={64}
          justifyContent="center"
          opacity={hasNeverContributed ? 0.7 : streakMuted ? 0.95 : 1}
          width={64}
        >
          <Column
            alignItems="center"
            borderRadius="radius.full"
            justifyContent="center"
            style={
              (streakMuted && !showIceCube) || hasNeverContributed
                ? undefined
                : {
                    shadowColor: showIceCube ? '#5189D7' : '#D78151',
                    shadowOffset: { width: 0, height: 0 },
                    shadowOpacity: 0.5,
                    shadowRadius: 12,
                  }
            }
          >
            <Text fontSize={32}>{headerEmoji}</Text>
          </Column>
        </Column>
      </Row>

      <DaysRow
        currentDay={currentDay}
        completedDays={completedDays}
        missedDays={missedDays}
        totalDays={totalDays}
        showIceCube={showIceCube}
        hasContributedToday={hasContributedToday}
      />
    </Column>
  );
}

function DailyContributionCard({
  hasContributedToday,
  nextActionType,
  onContributePress,
}: Readonly<{
  hasContributedToday: boolean;
  nextActionType: DailyActionType | null;
  onContributePress: () => void;
}>) {
  const { t } = useTranslation('streak');
  return (
    <Column
      backgroundColor="white"
      borderRadius="radius.lg"
      gap="spacing.general.x4"
      padding="spacing.general.x4"
      borderWidth={1}
      borderColor="surface.disabled"
    >
      <Text fontSize={16} fontWeight="bold">
        {t('contributeDailyTitle')}
      </Text>

      <Button
        disabled={hasContributedToday || !nextActionType}
        onPress={onContributePress}
        variant="primary"
        style={[
          { borderRadius: 30, minHeight: 50 },
          hasContributedToday && {
            backgroundColor: 'white',
            borderColor: theme.colors['surface.inverse.tertiary-neutral'],
            borderWidth: 1,
          },
        ]}
      >
        <Text color={hasContributedToday ? 'surface.success.default' : 'white'} fontSize={15} fontWeight="600">
          {hasContributedToday ? t('alreadyContributedToday') : t('makeMyDailyContribution')}
        </Text>
      </Button>
    </Column>
  );
}

function DailyProgress() {
  const { t } = useTranslation('streak');
  return (
    <Column gap="spacing.general.x2">
      <Text fontSize={18} fontWeight="bold">
        {t('myProgress')}
      </Text>

      <Row
        alignItems="center"
        borderRadius="radius.lg"
        gap="spacing.general.x4"
        padding="spacing.general.x4"
        style={{ backgroundColor: '#F7E9AB' }}
      >
        <Text fontSize={22}>💡</Text>
        <Text flex={1} fontSize={13} lineHeight={18}>
          <Trans
            components={{ strong: <Text fontWeight="bold" /> }}
            i18nKey="neighbourhoodFirstShare"
            ns="streak"
            values={{ percent: NEIGHBOURHOOD_FIRST_SHARE_PERCENT }}
          />
        </Text>
      </Row>

      <TipsCard />
    </Column>
  );
}

function ContributionDetailsSheet({ onClose, open }: Readonly<{ onClose: () => void; open: boolean }>) {
  const { t } = useTranslation('streak');
  return (
    <BottomSheet open={open} onClose={onClose} showIndicator={false}>
      <Column gap="spacing.general.x8" paddingHorizontal="spacing.general.x4" paddingBottom="spacing.general.x4">
        <Row alignItems="center" justifyContent="space-between">
          <Pressable hitSlop={10} onPress={onClose} style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}>
            <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} />
          </Pressable>
          <Text flex={1} fontWeight="600" textAlign="center" variant="headerXSmall">
            {t('contributionDetailsTitle')}
          </Text>
        </Row>
        <Text fontSize={16}>{t('contributionDetails')}</Text>
      </Column>
    </BottomSheet>
  );
}

export const DailyContributionsScreen = function DailyContributionsScreen({
  navigation,
}: DailyContributionsScreenProps) {
  const { t } = useTranslation('streak');
  const { refetch: refetchUser } = useUser();
  const { refetch: refetchStats } = useDailyStats({ enabled: !STREAK_DEV_MOCK_ENABLED });
  const streakUi = useStreakContributionUi();
  const { isLargeUI } = useUISize();

  const {
    numStreak,
    hasContributedToday,
    streakMuted,
    completedDays,
    missedDays,
    currentDay,
    totalDays,
    showIceCube,
    hasNeverContributed,
    effectiveDailyStats,
  } = streakUi;

  useFocusEffect(() => {
    if (!STREAK_DEV_MOCK_ENABLED) {
      void refetchUser();
      void refetchStats();
    }
  });

  const [showNextPage, setShowNextPage] = useStoredValue(StorageKey.StreakFlameIgnited, false);

  const headerBg = getDailyHeaderBackground(showIceCube, streakMuted, hasNeverContributed);

  const actionTypes = effectiveDailyStats?.actionTypes ?? [];

  // Hide the navigation header on the empty "ignite the flame" state, keep it otherwise.
  // Always show the header in largeUI mode to keep the top menu bar visible.
  const isEmptyState = !hasContributedToday && !showNextPage;
  useNavigationOptions(
    navigation,
    {
      headerShown: isLargeUI || !isEmptyState,
      headerStyle: { backgroundColor: headerBg },
      headerTitle: t('headerTitleDaily'),
    },
    false,
  );

  const nextActionType: DailyActionType | null = actionTypes[0] ?? null;

  const [activeKey, setActiveKey] = useState<DailyActionType | null>(null);
  const [open, setOpen] = useState(false);
  const onContributePress = () => {
    if (nextActionType) setActiveKey(nextActionType);
  };

  const onChangeActionType = (actionType: DailyActionType) => {
    const currentIndex = actionTypes.indexOf(actionType);
    if (currentIndex === -1) {
      return;
    }
    const nextIndex = currentIndex + 1;
    if (nextIndex >= actionTypes.length) {
      const firstActionType = actionTypes[0];
      if (!firstActionType) return;
      setActiveKey(firstActionType);
      return;
    }
    const _nextActionType = actionTypes[nextIndex];
    if (!_nextActionType) return;
    setActiveKey(_nextActionType);
  };

  const onCloseSheet = () => setActiveKey(null);

  if (isEmptyState) {
    return (
      <Page stretchToFill contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}>
        <EmptyStreakState onPress={() => setShowNextPage(true)} />
      </Page>
    );
  }

  return (
    <Page paddingStyle={{ paddingTop: 0, paddingHorizontal: 0 }} showsVerticalScrollIndicator={false}>
      <Column gap="spacing.general.x5" style={{ paddingBottom: theme.spacing['spacing.general.x10'] }}>
        <DailyHeader
          completedDays={completedDays}
          currentDay={currentDay}
          hasContributedToday={hasContributedToday}
          hasNeverContributed={hasNeverContributed}
          headerBackground={headerBg}
          missedDays={missedDays}
          numStreak={numStreak}
          onOpenDetails={() => setOpen(true)}
          showIceCube={showIceCube}
          streakMuted={streakMuted}
          totalDays={totalDays}
        />

        <Column paddingHorizontal="spacing.general.x4" gap="spacing.general.x5">
          <DailyContributionCard
            hasContributedToday={hasContributedToday}
            nextActionType={nextActionType}
            onContributePress={onContributePress}
          />
          <DailyProgress />
        </Column>
      </Column>

      <ContributionBottomSheet
        onChangeActionType={onChangeActionType}
        contributionKey={activeKey}
        navigation={navigation}
        onClose={onCloseSheet}
      />
      <ContributionDetailsSheet onClose={() => setOpen(false)} open={open} />
    </Page>
  );
};
